// src/components/blog/BlogPostSkeleton.jsx - Premium Animated Blog Post Skeleton
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';

// Animation variants for staggered items
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
};

function BlogPostSkeleton() {
  // Inject CSS animations into document head
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes shimmer {
        0% {
          transform: translateX(-100%);
        }
        100% {
          transform: translateX(100%);
        }
      }

      @keyframes pulse-subtle {
        0%, 100% {
          opacity: 0.4;
        }
        50% {
          opacity: 0.8;
        }
      }

      @keyframes bounce-skeleton {
        0%, 60%, 100% {
          transform: translateY(0);
        }
        30% {
          transform: translateY(-8px);
        }
      }

      @keyframes gradient-shift {
        0% {
          background-position: 0% 50%;
        }
        50% {
          background-position: 100% 50%;
        }
        100% {
          background-position: 0% 50%;
        }
      }

      @keyframes scale-pulse {
        0%, 100% {
          transform: scale(1);
        }
        50% {
          transform: scale(0.95);
        }
      }

      .animate-shimmer {
        animation: shimmer 1.8s infinite;
      }

      .animate-pulse-subtle {
        animation: pulse-subtle 1.5s ease-in-out infinite;
      }

      .animate-bounce-skeleton {
        animation: bounce-skeleton 1.4s infinite ease-in-out;
      }

      .animate-gradient-shift {
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }

      .animate-scale-pulse {
        animation: scale-pulse 2s ease-in-out infinite;
      }

      .skeleton-base {
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
        border-radius: 8px;
      }

      .skeleton-card {
        background: var(--bg);
        border: 1px solid var(--border);
        border-radius: 12px;
        overflow: hidden;
        position: relative;
      }

      .skeleton-card .shimmer-overlay {
        position: absolute;
        inset: 0;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.05) 30%,
          rgba(255, 255, 255, 0.08) 50%,
          rgba(255, 255, 255, 0.05) 70%,
          transparent 100%
        );
        animation: shimmer 2s infinite;
        pointer-events: none;
      }

      .dark .skeleton-card .shimmer-overlay {
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.03) 30%,
          rgba(255, 255, 255, 0.05) 50%,
          rgba(255, 255, 255, 0.03) 70%,
          transparent 100%
        );
      }

      .skeleton-image {
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
        position: relative;
        overflow: hidden;
      }

      .skeleton-image .shimmer-overlay {
        position: absolute;
        inset: 0;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.06) 30%,
          rgba(255, 255, 255, 0.1) 50%,
          rgba(255, 255, 255, 0.06) 70%,
          transparent 100%
        );
        animation: shimmer 2s infinite;
        pointer-events: none;
      }

      .dark .skeleton-image .shimmer-overlay {
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.03) 30%,
          rgba(255, 255, 255, 0.05) 50%,
          rgba(255, 255, 255, 0.03) 70%,
          transparent 100%
        );
      }

      .skeleton-text {
        height: 12px;
        border-radius: 4px;
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }

      .skeleton-text-sm {
        height: 10px;
        border-radius: 3px;
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }

      .skeleton-text-lg {
        height: 20px;
        border-radius: 6px;
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }

      .skeleton-circle {
        border-radius: 50%;
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }

      .skeleton-badge {
        border-radius: 9999px;
        background: linear-gradient(
          135deg,
          rgba(0,0,0,0.3) 0%,
          rgba(0,0,0,0.5) 50%,
          rgba(0,0,0,0.3) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }

      .skeleton-pill {
        border-radius: 9999px;
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="min-h-screen"
      style={{ backgroundColor: 'var(--bg)' }}
    >
      {/* Sticky Navigation Skeleton */}
      <div
        className="sticky top-[85px] z-30 backdrop-blur-md border-b"
        style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
      >
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <motion.div
            variants={itemVariants}
            className="flex items-center gap-2"
          >
            <div className="skeleton-text-lg w-24" />
          </motion.div>
          <motion.div
            variants={itemVariants}
            className="flex items-center gap-3"
          >
            <div className="skeleton-text w-20 h-8 rounded-full" />
            <div className="skeleton-text w-16 h-8 rounded-full" />
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column - Main Content (8 columns) */}
          <div className="lg:col-span-8 space-y-6">
            {/* Featured Image Skeleton */}
            <motion.div
              variants={itemVariants}
              className="skeleton-card rounded-2xl overflow-hidden"
            >
              <div className="relative w-full pt-[56.25%] skeleton-image">
                <div className="shimmer-overlay" />
                {/* Image caption skeleton */}
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
                  <div
                    className="skeleton-text-sm w-48 mx-auto"
                    style={{ background: 'rgba(255,255,255,0.3)' }}
                  />
                </div>
              </div>
            </motion.div>

            {/* Title & Meta Skeleton */}
            <motion.div variants={itemVariants} className="space-y-3">
              <div className="skeleton-text-lg w-3/4" />
              <div className="flex flex-wrap gap-3">
                <div className="skeleton-text-sm w-28" />
                <div className="skeleton-text-sm w-20 rounded-full" />
                <div className="skeleton-text-sm w-20" />
                <div className="skeleton-text-sm w-24" />
                <div className="skeleton-text-sm w-32" />
              </div>
            </motion.div>

            {/* Excerpt Skeleton */}
            <motion.div
              variants={itemVariants}
              className="p-4 rounded-xl"
              style={{ backgroundColor: 'var(--border-light)' }}
            >
              <div className="skeleton-text w-full" />
              <div className="skeleton-text w-11/12 mt-1" />
              <div className="skeleton-text w-10/12 mt-1" />
            </motion.div>

            {/* Table of Contents Skeleton */}
            <motion.div
              variants={itemVariants}
              className="p-4 rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="skeleton-circle w-4 h-4" />
                  <div className="skeleton-text-lg w-40" />
                </div>
                <div className="skeleton-circle w-4 h-4" />
              </div>
              <div
                className="mt-3 pt-3 border-t"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="space-y-1.5">
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className="skeleton-text-sm w-3/4"
                      style={{ marginLeft: i > 0 ? '16px' : '0' }}
                    />
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Content Skeleton */}
            <motion.div variants={itemVariants} className="space-y-3">
              <div className="skeleton-text w-full" />
              <div className="skeleton-text w-11/12" />
              <div className="skeleton-text w-full" />
              <div className="skeleton-text w-10/12" />
              <div className="skeleton-text w-full" />
              <div className="skeleton-text w-9/12" />
              <div className="skeleton-text w-full" />
              <div className="skeleton-text w-11/12" />
            </motion.div>

            {/* Author Bio Skeleton */}
            <motion.div
              variants={itemVariants}
              className="p-4 rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex items-start gap-4">
                <div className="skeleton-circle w-12 h-12 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="skeleton-text-sm w-32" />
                  <div className="skeleton-text-sm w-full" />
                  <div className="skeleton-text-sm w-3/4" />
                </div>
              </div>
            </motion.div>

            {/* Related Posts Skeleton */}
            <motion.div
              variants={itemVariants}
              className="mt-8 pt-6 border-t"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="skeleton-text w-1 h-6" />
                <div className="skeleton-text-lg w-48" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[...Array(3)].map((_, idx) => (
                  <div
                    key={idx}
                    className="skeleton-card rounded-xl overflow-hidden border"
                    style={{ borderColor: 'var(--border)' }}
                  >
                    <div className="skeleton-image h-36">
                      <div className="shimmer-overlay" />
                    </div>
                    <div className="p-3 space-y-2">
                      <div className="skeleton-text-sm w-20" />
                      <div className="skeleton-text-sm w-full" />
                      <div className="skeleton-text-sm w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Comment Section Skeleton */}
            <motion.div
              variants={itemVariants}
              className="pt-4 border-t"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="skeleton-text-lg w-40 mb-4" />
              <div className="space-y-4">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="skeleton-circle w-10 h-10 flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="skeleton-text-sm w-24" />
                        <div className="skeleton-text-sm w-20" />
                      </div>
                      <div className="skeleton-text-sm w-full" />
                      <div className="skeleton-text-sm w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Inline Ad Skeleton */}
            <motion.div
              variants={itemVariants}
              className="p-3 rounded-xl border border-dashed text-center"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="skeleton-text-sm w-32 h-4 mx-auto" />
              <div className="skeleton-image min-h-[60px] w-full mt-2 rounded-lg" />
            </motion.div>
          </div>

          {/* Right Column - Sidebar (4 columns) */}
          <div className="lg:col-span-4">
            <div className="sticky top-24 space-y-6">
              {/* Fiverr Card Skeleton */}
              <motion.div
                variants={itemVariants}
                className="p-4 rounded-xl border"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="flex items-start gap-4">
                  <div className="skeleton-circle w-14 h-14 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="skeleton-text-lg w-32" />
                      <div className="skeleton-text w-16 h-4 rounded-full" />
                    </div>
                    <div className="skeleton-text-sm w-48" />
                    <div className="flex items-center gap-2">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="skeleton-circle w-3 h-3" />
                      ))}
                      <div className="skeleton-text-sm w-12" />
                      <div className="skeleton-text-sm w-20" />
                    </div>
                    <div className="skeleton-text w-32 h-8 rounded-lg" />
                  </div>
                </div>
              </motion.div>

              {/* Google Ad Space - Top Sidebar Skeleton */}
              <motion.div
                variants={itemVariants}
                className="p-3 rounded-xl border border-dashed text-center"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="skeleton-text-sm w-32 h-4 mx-auto" />
                <div className="skeleton-image min-h-[250px] w-full mt-2 rounded-lg" />
              </motion.div>

              {/* Popular Posts Skeleton */}
              <motion.div
                variants={itemVariants}
                className="p-4 rounded-xl border"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="skeleton-circle w-4 h-4" />
                  <div className="skeleton-text-lg w-32" />
                </div>
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="skeleton-text-sm w-5" />
                      <div className="flex-1 space-y-1">
                        <div className="skeleton-text-sm w-full" />
                        <div className="skeleton-text-sm w-20" />
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Google Ad Space - Middle Sidebar Skeleton */}
              <motion.div
                variants={itemVariants}
                className="p-3 rounded-xl border border-dashed text-center"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="skeleton-text-sm w-32 h-4 mx-auto" />
                <div className="skeleton-image min-h-[200px] w-full mt-2 rounded-lg" />
              </motion.div>

              {/* Categories Skeleton */}
              <motion.div
                variants={itemVariants}
                className="p-4 rounded-xl border"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="skeleton-circle w-4 h-4" />
                  <div className="skeleton-text-lg w-32" />
                </div>
                <div className="flex flex-wrap gap-2">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="skeleton-pill w-20 h-7" />
                  ))}
                </div>
              </motion.div>

              {/* Tags Skeleton */}
              <motion.div
                variants={itemVariants}
                className="p-4 rounded-xl border"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="skeleton-circle w-4 h-4" />
                  <div className="skeleton-text-lg w-32" />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {[...Array(10)].map((_, i) => (
                    <div key={i} className="skeleton-pill w-16 h-6" />
                  ))}
                </div>
              </motion.div>

              {/* Google Ad Space - Bottom Sidebar Skeleton */}
              <motion.div
                variants={itemVariants}
                className="p-3 rounded-xl border border-dashed text-center"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="skeleton-text-sm w-32 h-4 mx-auto" />
                <div className="skeleton-image min-h-[150px] w-full mt-2 rounded-lg" />
              </motion.div>

              {/* Contact CTA Skeleton */}
              <motion.div
                variants={itemVariants}
                className="p-4 rounded-xl border text-center"
                style={{
                  borderColor: 'var(--border)',
                  backgroundColor: 'var(--border-light)',
                }}
              >
                <div className="skeleton-circle w-12 h-12 mx-auto mb-2" />
                <div className="skeleton-text-lg w-40 mx-auto mb-1" />
                <div className="skeleton-text-sm w-48 mx-auto mb-3" />
                <div className="skeleton-text w-32 h-8 mx-auto rounded-full" />
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Bounce Indicator */}
      <motion.div
        variants={itemVariants}
        className="flex items-center justify-center gap-3 pb-8"
      >
        <div
          className="w-2.5 h-2.5 rounded-full animate-bounce-skeleton"
          style={{
            backgroundColor: 'var(--text)',
            animationDelay: '0s',
          }}
        />
        <div
          className="w-2.5 h-2.5 rounded-full animate-bounce-skeleton"
          style={{
            backgroundColor: 'var(--text)',
            animationDelay: '0.2s',
          }}
        />
        <div
          className="w-2.5 h-2.5 rounded-full animate-bounce-skeleton"
          style={{
            backgroundColor: 'var(--text)',
            animationDelay: '0.4s',
          }}
        />
      </motion.div>
    </motion.div>
  );
}

export default BlogPostSkeleton;
